import { useCallback, useEffect, useState } from 'react';
import type { Canal, Incidencia } from '../../api/ops/canales';
import { Boton } from '../../componentes/Boton';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { ListaCanales } from './ListaCanales';
import { PlantillasMeta } from './PlantillasMeta';
import { avisoCaido, esTipoDibujado, TIPO, vivo, type Bloques } from './reglas';
import '../../componentes/apartado.css';
import '../avisos/avisos.css';
import './canales.css';

/**
 * «Canales» (Configuración, entrada `canales`; 11.1, 11.2 y 11.4): la barra Canales · Plantillas de Meta en `?apartado=`
 * con los apartados que la persona puede ver (Canales con `canales.salud.ver` o `canales.canales.gestionar`; Plantillas
 * de Meta con `canales.plantillas.gestionar`). Arriba de los dos, el aviso rojo de cada canal caído (`CanalCaido`, G451)
 * con «Ver el canal». Los dos apartados comparten la lista de canales, sus incidencias abiertas, los bloques activos
 * (`listEntitlements`, `negocio.ver`: qué bloque necesita cada tipo) y las sedes.
 */
export interface DatosCanales {
    canales: Canal[];
    /** La incidencia abierta más reciente de cada canal (o null). */
    abiertas: Readonly<Record<string, Incidencia | null>>;
    bloques: Bloques;
    sedes: { id: string; nombre: string }[];
}

export interface PropsApartado extends PropsPantalla {
    datos: DatosCanales;
    recargarDatos: () => void;
    irA: (apartado: 'canales' | 'plantillas', extra?: string) => void;
}

const APARTADOS = [
    { valor: 'canales', etiqueta: 'Canales', corta: 'Canales', icono: 'messages-square' },
    { valor: 'plantillas', etiqueta: 'Plantillas de Meta', corta: 'Plantillas', icono: 'message-square-text' },
] as const;

export function Canales(props: PropsPantalla) {
    const { ops, negocio, busqueda, ruta, navegar, movil, membresia, nombreNegocio } = props;
    const { puede } = usePermisos();
    const veCanales = puede('canales.salud.ver') || puede('canales.canales.gestionar');
    const vePlantillas = puede('canales.plantillas.gestionar');
    const visibles = APARTADOS.filter((a) => (a.valor === 'canales' ? veCanales : vePlantillas));
    const activo = visibles.find((a) => a.valor === busqueda.get('apartado')) ?? visibles[0];
    const [datos, setDatos] = useState<DatosCanales | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const recargarDatos = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (visibles.length === 0) {
            return;
        }
        let vivoEfecto = true;
        Promise.all([
            ops.listChannels(negocio).catch((e: unknown) => {
                // Sin `canales.salud.ver` (solo plantillas), sin canales que enseñar; con él, el error es de la página.
                if (veCanales) {
                    throw e;
                }
                return { data: [] as Canal[] };
            }),
            ops.listEntitlements(negocio).catch(() => null),
            ops.listLocations(negocio, { limit: 100 }).catch(() => null),
        ])
            .then(async ([lista, bloques, sedes]) => {
                const canales = lista.data.filter((c) => esTipoDibujado(c.channel_type));
                const abiertas: Record<string, Incidencia | null> = {};
                await Promise.all(
                    // El chat de la web no tiene salud ni incidencias (G715).
                    canales.filter((c) => vivo(c) && c.channel_type !== 'web_chat').map((c) =>
                        ops
                            .listChannelIncidents(negocio, c.id, { open: true, limit: 1 })
                            .then((r) => {
                                abiertas[c.id] = r.data[0] ?? null;
                            })
                            .catch(() => {
                                abiertas[c.id] = null;
                            }),
                    ),
                );
                if (!vivoEfecto) {
                    return;
                }
                setDatos({
                    canales,
                    abiertas,
                    bloques: bloques === null ? null : new Set(bloques.entitlements.map((b) => b.product_code)),
                    sedes: (sedes?.data ?? []).filter((s) => s.status === 'active').map((s) => ({ id: s.id, nombre: s.name })),
                });
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivoEfecto) {
                    setError(e);
                }
            });
        return () => {
            vivoEfecto = false;
        };
    }, [ops, negocio, intento, veCanales, visibles.length]);

    if (activo === undefined) {
        return <EstadoSinPermiso seccion="los canales" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null) {
        return <EstadoError seccion={activo.valor === 'plantillas' ? 'las plantillas' : 'los canales'} codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargarDatos} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion={activo.valor === 'plantillas' ? 'las plantillas de Meta' : 'los canales'} />;
    }
    const base = ruta.split('?')[0] ?? ruta;
    const irA = (apartado: 'canales' | 'plantillas', extra?: string) => {
        navegar(`${rutaConApartado(base, apartado)}${extra === undefined ? '' : `&${extra}`}`);
    };
    const caidos = veCanales ? datos.canales.filter((c) => vivo(c) && c.status === 'down') : [];
    const Apartado = activo.valor === 'canales' ? ListaCanales : PlantillasMeta;

    return (
        <div className="rv-en-pantalla">
            {caidos.map((c) => {
                const a = avisoCaido(c, datos.abiertas[c.id] ?? null, diaYHora);
                return (
                    <div key={c.id} className="rv-cn-caido" role="alert">
                        <span className="rv-cn-caido__baldosa">
                            <Icono nombre={TIPO[c.channel_type]?.icono ?? 'messages-square'} />
                        </span>
                        <span className="rv-cn-caido__texto">
                            <strong>{a.titulo}</strong>
                            <span>{a.texto}</span>
                        </span>
                        <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { irA('canales', `canal=${encodeURIComponent(c.id)}`); }}>
                            Ver el canal
                        </Boton>
                    </div>
                );
            })}
            {visibles.length > 1 && (
                <div className="rv-en-barra rv-cn-barra">
                    <Segmentos
                        etiqueta="Apartados de canales"
                        movil={movil}
                        items={visibles.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono }))}
                        valor={visibles.indexOf(activo)}
                        onCambio={(i) => {
                            const a = visibles[i];
                            if (a !== undefined) {
                                irA(a.valor);
                            }
                        }}
                    />
                </div>
            )}
            <Apartado key={activo.valor} {...props} datos={datos} recargarDatos={recargarDatos} irA={irA} />
        </div>
    );
}
